<template>
  <div>
    <b-loading :is-full-page="true" :active.sync="isLoading"> </b-loading>
    <table id="ni-general-info-table-eu">
      <thead>
        <tr>
          <th>Line No.</th>
          <th class="select-checkbox-cell">
            <b-checkbox
              :disabled="showSubmissionInReadOnly"
              v-model="selectAllChecked"
              type="is-info"
              size="is-large"
              title="Select All"
            ></b-checkbox>
          </th>
          <th class="no-header-label"></th>
          <th
            v-for="columnData in generalInfoFields.columns"
            :key="columnData.id"
            :class="columnData.className"
            :title="columnData.title"
          >
            <label>{{ columnData.label }}</label>
          </th>
        </tr>
      </thead>
      <tbody class="ni-app-general-info-tbody-eu ni-app-general-info-tbody">
        <tr
          v-for="(row, index) in rows"
          :key="row.id"
          :class="[
            row.genlInfoId ? 'row-' + row.genlInfoId : 'newRow',
            row.itemStatus === 'Rework – Additional Data Required'
              ? 'rework-item'
              : ''
          ]"
          @click="onRowClick($event)"
          @change="!isLoading ? changeOnRow($event, index, row) : ''"
        >
          <ValidationObserver>
            <td class="line-number">{{ row.lineNumber }}</td>
            <td class="select-checkbox-cell">
              <b-checkbox
                :disabled="showSubmissionInReadOnly"
                type="is-info"
                size="is-large"
                v-model="checkboxGroup"
                :native-value="row.genlInfoId"
              ></b-checkbox>
            </td>
            <td class="copy-item-option-cell" :value="row">
              <b-button
                type="is-info"
                title="Copy Item"
                @click="copyRowItems(row)"
                :disabled="
                  !row.genlInfoId ||
                    row.itemStatus === 'Rework – Additional Data Required'
                "
                outlined
                >Copy</b-button
              >
            </td>
            <td class="line-errors">{{ row.lineErrors }}</td>
            <td>
              <b-input
                class="totalLineErrorCount"
                disabled
                v-model="row.totalLineErrorCount"
              >
              </b-input>
            </td>
            <td class="supp-item-nbr">
              <b-tooltip
                class="supp-item-nbr-tooltip"
                v-show="suppItemNbrRequired && tooltipIndex === index"
                label="Supplier Item No. is required to save this record"
                always
                position="is-left"
                type="is-danger"
                multilined
              >
              </b-tooltip>
              <b-input
                class="suppItemNbr"
                v-model="row.suppItemNbr"
                oninput='this.value = this.value.replace(/[,"]+/g, ""); if(this.value.length > 25) this.value = this.value.slice(0, 25)'
                @change.native="
                  !row.genlInfoId
                    ? insertNewRow($event.target.value, index)
                    : ''
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'suppItemNbr',
                    row.suppItemNbr
                  )
                "
              ></b-input>
            </td>
            <td class="item-status-cell">
              <b-input
                class="itemStatus"
                v-model="row.itemStatus"
                disabled
                :title="row.itemStatus"
              ></b-input>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
                vid="productCategory"
              >
                <div class="control" :class="classes">
                  <b-select
                    class="productCategory"
                    v-model="row.productCategory"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'productCategory',
                        row.productCategory
                      )
                    "
                  >
                    <option value="Equipment and Instruments"
                      >Equipment and Instruments</option
                    >
                    <option value="Biologicals">Biologicals</option>
                    <option value="Chemicals and Biochemical Reagents"
                      >Chemicals and Biochemical Reagents</option
                    >
                    <option value="Consumables">Consumables</option>
                    <option value="Diagnostics">Diagnostics</option>
                    <option value="Furniture">Furniture</option>
                    <option value="Training and Services"
                      >Training and Services</option
                    >
                    <option value="Safety Products">Safety Products</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="manufacturerItemNbr"
                    @change.native="
                      row.manufacturerItemNbr = $event.target.value
                    "
                    oninput='this.value = this.value.replace(/[,"]+/g, "");'
                    :value="row.manufacturerItemNbr"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'manufacturerItemNbr',
                        row.manufacturerItemNbr
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td v-on:keypress="typeAheadTriggered = 'manufacturerName'">
              <v-select
                :filterable="false"
                :options="manufacturerNameOptions"
                class="manufacturerName"
                v-bind:class="[
                  !row.manufacturerName ? 'danger' : '',
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'manufacturerName',
                    row.manufacturerName
                  )
                ]"
                clearable
                v-model="row.manufacturerName"
                @search="fetchSupplierTypeAhead"
                @input="changeOnRow($event, index, row)"
                :title="row.manufacturerName"
                id="newManufacturer"
              >
                <template slot="option" slot-scope="manufacturerNameOption">
                  <div class="d-center">
                    {{ manufacturerNameOption.label }}
                  </div>
                </template>
                <template
                  slot="selected-option"
                  slot-scope="manufacturerNameOption"
                  title="manufacturerNameOption"
                >
                  <div class="selected d-center">
                    {{ manufacturerNameOption.label }}
                  </div>
                </template>
                <template slot="no-options" slot-scope="{ search, searching }">
                  <template v-if="searching">
                    Sorry, no matching options.
                  </template>
                  <span v-else class="display-none"></span>
                </template>
              </v-select>
            </td>
            <td v-on:keypress="typeAheadTriggered = 'suppBrandName'">
              <v-select
                :filterable="false"
                :options="suppBrandNameOptions"
                class="suppBrandName"
                v-bind:class="[
                  !row.suppBrandName ? 'danger' : '',
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'suppBrandName',
                    row.suppBrandName
                  )
                ]"
                v-model="row.suppBrandName"
                @search="fetchSupplierTypeAhead"
                @input="changeOnRow($event, index, row)"
                :title="row.suppBrandName"
                id="newSuppBrand"
              >
                <template slot="option" slot-scope="suppBrandNameOption">
                  <div class="d-center">
                    {{ suppBrandNameOption.label }}
                  </div>
                </template>
                <template
                  slot="selected-option"
                  slot-scope="suppBrandNameOption"
                  title="suppBrandNameOption"
                >
                  <div class="selected d-center">
                    {{ suppBrandNameOption.label }}
                  </div>
                </template>
                <template slot="no-options" slot-scope="{ search, searching }">
                  <template v-if="searching">
                    Sorry, no matching options.
                  </template>
                  <span v-else class="display-none"></span>
                </template>
              </v-select>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    class="mainPackType"
                    v-model="row.mainPackType"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'mainPackType',
                        row.mainPackType
                      )
                    "
                  >
                    <option value="CS">CS</option>
                    <option value="DZ">DZ</option>
                    <option value="EA">EA</option>
                    <option value="GR">GR</option>
                    <option value="PK">PK</option>
                    <option value="PL">PL</option>
                    <option value="PR">PR</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required|min_value:0.01"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="mainPackSize"
                    oninput="this.value = this.value.replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{2})./g, '$1');if(this.value.split('.')[0].length > 14) this.value = this.value.split('.')[0].slice(0, 14) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.mainPackSize = $event.target.value"
                    :value="
                      row.mainPackSize !== undefined
                        ? addTwoZeros(row.mainPackSize)
                        : ''
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'mainPackSize',
                        row.mainPackSize
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    class="mainPackUnit"
                    v-model="row.mainPackUnit"
                    :value="
                      row.mainPackUnit === undefined
                        ? (row.mainPackUnit = ' ')
                        : row.mainPackUnit
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'mainPackUnit',
                        row.mainPackUnit
                      )
                    "
                    :title="row.mainPackUnit"
                  >
                    <option value=" "></option>
                    <option
                      v-for="option in mainPackUnitOptions"
                      :value="option"
                      :key="option.id"
                      >{{ option }}</option
                    >
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                :bails="false"
                v-slot="{ classes }"
                immediate
                vid="altPackSizeType"
              >
                <div class="control" :class="classes">
                  <b-select
                    class="altPackSizeType"
                    v-model="row.altPackSizeType"
                    :value="
                      row.altPackSizeType === undefined
                        ? (row.altPackSizeType = ' ')
                        : row.altPackSizeType
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'altPackSizeType',
                        row.altPackSizeType
                      )
                    "
                  >
                    <option value=" "></option>
                    <option value="CS">CS</option>
                    <option value="DZ">DZ</option>
                    <option value="EA">EA</option>
                    <option value="GR">GR</option>
                    <option value="PK">PK</option>
                    <option value="PL">PL</option>
                    <option value="PR">PR</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:altPackSizeType|min_value:1.1"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="altPackSizeConv"
                    oninput="this.value = this.value.replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{2})./g, '$1');if(this.value.split('.')[0].length > 14) this.value = this.value.split('.')[0].slice(0, 14) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.altPackSizeConv = $event.target.value"
                    :value="
                      row.altPackSizeConv !== undefined
                        ? addTwoZeros(row.altPackSizeConv)
                        : ''
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'altPackSizeConv',
                        row.altPackSizeConv
                      )
                    "
                    :disabled="
                      row.altPackSizeType === undefined ||
                        row.altPackSizeType === ' '
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required|max:94"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="englishPrdctNoun"
                    @input="row.englishPrdctNoun = sanitizeProductionDescription($event)"
                    @change.native="row.englishPrdctNoun = $event.target.value"
                    :value="row.englishPrdctNoun"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'englishPrdctDesc',
                        row.englishPrdctNoun
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:addPrdctNoun"
                :bails="false"
                v-slot="{ classes }"
                immediate
                vid="addLangPrdctDessc"
              >
                <div class="control" :class="classes">
                  <b-select
                    class="addLangPrdctDessc"
                    v-model="row.addLangPrdctDessc"
                    :value="
                      row.addLangPrdctDessc === undefined
                        ? (row.addLangPrdctDessc = ' ')
                        : row.addLangPrdctDessc
                    "
                    :class="[
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'addLangCode',
                        row.addLangPrdctDessc
                      )
                    ]"
                  >
                    <option value=" "></option>
                    <option value="BD">BD</option>
                    <option value="BR">BR</option>
                    <option value="CH">CH</option>
                    <option value="DA">DA</option>
                    <option value="DE">DE</option>
                    <option value="ES">ES</option>
                    <option value="FI">FI</option>
                    <option value="FR">FR</option>
                    <option value="IT">IT</option>
                    <option value="NL">NL</option>
                    <option value="NO">NO</option>
                    <option value="PL">PL</option>
                    <option value="PT">PT</option>
                    <option value="SE">SE</option>
                    <option value="SL">SL</option>
                    <option value="SP">SP</option>
                    <option value="ST">ST</option>
                    <option value="SV">SV</option>
                    <option value="X2">X2</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:addLangPrdctDessc|max:94"
                :bails="false"
                v-slot="{ classes }"
                immediate
                vid="addPrdctNoun"
              >
                <div class="control" :class="classes">
                  <b-input
                    class="addPrdctNoun"
                    @input="row.addPrdctNoun = sanitizeProductionDescription($event)"
                    @change.native="row.addPrdctNoun = $event.target.value"
                    :disabled="
                      (row.addLangPrdctDessc === undefined ||
                        row.addLangPrdctDessc === ' ') &&
                        row.addPrdctNoun === undefined
                    "
                    :value="row.addPrdctNoun"
                    :class="[
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'addPrdctDesc',
                        row.addPrdctNoun
                      )
                    ]"
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="minOrdrQty"
                    oninput="this.value = this.value.replace(/[^0-9]/g, '');"
                    @change.native="row.minOrdrQty = $event.target.value"
                    :value="row.minOrdrQty"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'minOrdrQty',
                        row.minOrdrQty
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="suppLeadTime"
                    oninput="this.value = this.value.replace(/[^0-9]/g, '');"
                    @change.native="row.suppLeadTime = $event.target.value"
                    :value="row.suppLeadTime"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'suppLeadTime',
                        row.suppLeadTime
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-input
                class="minStorageTemp"
                @change.native="row.minStorageTemp = $event.target.value"
                @input.native="validateStorageTempInput($event,row,'minStorageTemp')"
                :value="row.minStorageTemp"
                :class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'minStorageTemp',
                    row.minStorageTemp
                  ),
                  row.minStorageTemp === undefined || row.minStorageTemp === ''
                    ? 'invalid'
                    : ''
                ]"
              ></b-input>
            </td>
            <td>
              <b-input
                class="maxStorageTemp"
                @change.native="row.maxStorageTemp = $event.target.value"
                @input.native="validateStorageTempInput($event,row,'maxStorageTemp')"
                :value="row.maxStorageTemp"
                :class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'maxStorageTemp',
                    row.maxStorageTemp
                  ),
                  row.maxStorageTemp === undefined || row.maxStorageTemp === ''
                    ? 'invalid'
                    : ''
                ]"
              ></b-input>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.coolant"
                    class="coolant"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'coolant',
                        row.coolant
                      )
                    "
                  >
                    <option value="Any">Any - Any</option>
                    <option value="Coolant/Kool-It"
                      >Coolant/Kool-It - Ice Packs</option
                    >
                    <option value="Dry Ice">Dry Ice - Dry Ice</option>
                    <option value="None">None - None</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-select
                class="sample"
                v-model="row.sample"
                :value="
                  row.sample === undefined ? (row.sample = ' ') : row.sample
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'sample',
                    row.sample
                  )
                "
              >
                <option value=" "></option>
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </b-select>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <v-select
                    :filterable="false"
                    :taggable="false"
                    :options="euCmmdtyCodeOptions"
                    class="euCmmdtyCode"
                    v-bind:class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'euCmmdtyCode',
                        row.euCmmdtyCode
                      )
                    "
                    v-model="row.euCmmdtyCode"
                    @search="fetchTypeAhead($event, 'euCmmdtyCode')"
                    @input="changeOnRow($event, index, row)"
                    :title="row.euCmmdtyCode"
                    :create-option="
                      euCmmdtyCodeOption => ({
                        label: euCmmdtyCodeOption
                      })
                    "
                    id="newEuCmmdtyCode"
                  >
                    <template slot="option" slot-scope="euCmmdtyCodeOption">
                      <div class="d-center">
                        {{ euCmmdtyCodeOption.label }}
                      </div>
                    </template>
                    <template
                      slot="selected-option"
                      slot-scope="euCmmdtyCodeOption"
                      title="euCmmdtyCodeOption"
                    >
                      <div class="selected d-center">
                        {{ euCmmdtyCodeOption.label }}
                      </div>
                    </template>
                    <template
                      slot="no-options"
                      slot-scope="{ search, searching }"
                    >
                      <template v-if="searching">
                        Sorry, no matching options.
                      </template>
                      <span v-else class="display-none"></span>
                    </template>
                  </v-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <v-select
                    icon-right="content-copy"
                    :filterable="false"
                    :taggable="false"
                    :options="countryOptions"
                    class="country"
                    v-bind:class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'country',
                        row.country
                      )
                    "
                    v-model="row.country"
                    @search="fetchTypeAhead($event, 'country')"
                    @input="changeOnRow($event, index, row)"
                    :title="row.country"
                  >
                    <template slot="option" slot-scope="countryOption">
                      <div class="d-center">
                        {{ countryOption.label }}
                      </div>
                    </template>
                    <template
                      slot="selected-option"
                      slot-scope="countryOption"
                      title="countryOption"
                    >
                      <div class="selected d-center">
                        {{ countryOption.label }}
                      </div>
                    </template>
                    <template
                      slot="no-options"
                      slot-scope="{ search, searching }"
                    >
                      <template v-if="searching">
                        Sorry, no matching options.
                      </template>
                      <span v-else class="display-none"></span>
                    </template>
                  </v-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.serialNbr"
                    class="serialNbr"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'serialNbr',
                        row.serialNbr
                      )
                    "
                    ><option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.batchCntrl"
                    class="batchCntrl"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'batchCntrl',
                        row.batchCntrl
                      )
                    "
                    ><option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-input
                class="shlfLf"
                oninput="this.value = this.value.replace(/[^0-9]/g, '');"
                @change.native="row.shlfLf = $event.target.value"
                :value="row.shlfLf"
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'shlfLf',
                    row.shlfLf
                  )
                "
              ></b-input>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:productCategory,Equipment,Instrument"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="warranty"
                    oninput="this.value = this.value.replace(/[^0-9]/g, '');"
                    @change.native="row.warranty = $event.target.value"
                    :value="row.warranty"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'warranty',
                        row.warranty
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.returnable"
                    class="returnable"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'returnable',
                        row.returnable
                      )
                    "
                    ><option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="mainUnitGrossWt"
                    oninput="this.value = this.value.replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.mainUnitGrossWt = $event.target.value"
                    :value="
                      row.mainUnitGrossWt !== undefined
                        ? addPercentMode(row.mainUnitGrossWt)
                        : row.mainUnitGrossWt
                    "
                    :class="[
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'mainUnitGrossWt',
                        row.mainUnitGrossWt
                      ),
                      row.mainUnitGrossWt &&
                      row.mainUnitNetWt &&
                      row.mainUnitGrossWt < row.mainUnitNetWt
                        ? 'invalid'
                        : ''
                    ]"
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="mainUnitNetWt"
                    oninput="this.value = this.value.replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.mainUnitNetWt = $event.target.value"
                    :value="
                      row.mainUnitNetWt !== undefined
                        ? addPercentMode(row.mainUnitNetWt)
                        : row.mainUnitNetWt
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'mainUnitNetWt',
                        row.mainUnitNetWt
                      )
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-input
                class="mainUnitGrossVol"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.mainUnitGrossVol = $event.target.value"
                :value="
                  row.mainUnitGrossVol !== undefined
                    ? addPercentMode(row.mainUnitGrossVol)
                    : ''
                "
                :class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'mainUnitGrossVol',
                    row.mainUnitGrossVol
                  ),
                  row.mainUnitGrossVol &&
                  row.mainUnitNetVol &&
                  row.mainUnitGrossVol < row.mainUnitNetVol
                    ? 'invalid'
                    : ''
                ]"
              ></b-input>
            </td>
            <td>
              <b-input
                class="mainUnitNetVol"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.mainUnitNetVol = $event.target.value"
                :value="
                  row.mainUnitNetVol !== undefined
                    ? addPercentMode(row.mainUnitNetVol)
                    : ''
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'mainUnitNetVol',
                    row.mainUnitNetVol
                  )
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="mainUnitPackLen"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.mainUnitPackLen = $event.target.value"
                :value="
                  row.mainUnitPackLen !== undefined
                    ? addPercentMode(row.mainUnitPackLen)
                    : row.mainUnitPackLen
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'mainUnitPackLen',
                    row.mainUnitPackLen
                  )
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="mainUnitPackHt"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.mainUnitPackHt = $event.target.value"
                :value="
                  row.mainUnitPackHt !== undefined
                    ? addPercentMode(row.mainUnitPackHt)
                    : row.mainUnitPackHt
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'mainUnitPackHt',
                    row.mainUnitPackHt
                  )
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="mainUnitPackWd"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.mainUnitPackWd = $event.target.value"
                :value="
                  row.mainUnitPackWd !== undefined
                    ? addPercentMode(row.mainUnitPackWd)
                    : ''
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'mainUnitPackWd',
                    row.mainUnitPackWd
                  )
                "
              ></b-input>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:altPackSizeType"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="altUnitGrossWt"
                    oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.altUnitGrossWt = $event.target.value"
                    :value="
                      row.altUnitGrossWt !== undefined
                        ? addPercentMode(row.altUnitGrossWt)
                        : row.altUnitGrossWt
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'altUnitGrossWt',
                        row.altUnitGrossWt
                      )
                    "
                    :disabled="
                      row.altPackSizeType === undefined ||
                        row.altPackSizeType === ' '
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:altPackSizeType"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-input
                    class="altUnitNetWt"
                    oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                    @change.native="row.altUnitNetWt = $event.target.value"
                    :value="
                      row.altUnitNetWt !== undefined
                        ? addPercentMode(row.altUnitNetWt)
                        : row.altUnitNetWt
                    "
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'altUnitNetWt',
                        row.altUnitNetWt
                      )
                    "
                    :disabled="
                      row.altPackSizeType === undefined ||
                        row.altPackSizeType === ' '
                    "
                  ></b-input>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-input
                class="altUnitGrossVol"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{5})./g, '$1');if(this.value.split('.')[0].length > 11) this.value = this.value.split('.')[0].slice(0, 11) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.altUnitGrossVol = $event.target.value"
                :value="
                  row.altUnitGrossVol !== undefined
                    ? addPercentMode(row.altUnitGrossVol)
                    : row.altUnitGrossVol
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'altUnitGrossVol',
                    row.altUnitGrossVol
                  )
                "
                :disabled="
                  row.altPackSizeType === undefined ||
                    row.altPackSizeType === ' '
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="altUnitPackLen"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.altUnitPackLen = $event.target.value"
                :value="
                  row.altUnitPackLen !== undefined
                    ? addPercentMode(row.altUnitPackLen)
                    : row.altUnitPackLen
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'altUnitPackLen',
                    row.altUnitPackLen
                  )
                "
                :disabled="
                  row.altPackSizeType === undefined ||
                    row.altPackSizeType === ' '
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="altUnitPackHt"
                oninput="this.value = this.value
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.altUnitPackHt = $event.target.value"
                :value="
                  row.altUnitPackHt !== undefined
                    ? addPercentMode(row.altUnitPackHt)
                    : row.altUnitPackHt
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'altUnitPackHt',
                    row.altUnitPackHt
                  )
                "
                :disabled="
                  row.altPackSizeType === undefined ||
                    row.altPackSizeType === ' '
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="altUnitPackWd"
                oninput="this.value = this.value
        .replace(/[^\d.]/g, '')
        .replace(/(\..*)\./g, '$1')
        .replace(/(\.[\d]{3})./g, '$1');if(this.value.split('.')[0].length > 9) this.value = this.value.split('.')[0].slice(0, 9) + (this.value.split('.')[1] ?  '.' + this.value.split('.')[1] : '');"
                @change.native="row.altUnitPackWd = $event.target.value"
                :value="
                  row.altUnitPackWd !== undefined
                    ? addPercentMode(row.altUnitPackWd)
                    : row.altUnitPackWd
                "
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'altUnitPackWd',
                    row.altUnitPackWd
                  )
                "
                :disabled="
                  row.altPackSizeType === undefined ||
                    row.altPackSizeType === ' '
                "
              ></b-input>
            </td>
            <td>
              <b-datepicker
                placeholder="Select a date..."
                icon="calendar-today"
                class="dateAvailable"
                :min-date="minDateAvailable"
                v-model="row.dateAvailable"
                @input="changeOnRow($event, index, row)"
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'dateAvailable',
                    row.dateAvailable
                  )
                "
              >
              </b-datepicker>
            </td>
            <td>
              <ValidationProvider
                :bails="false"
                v-slot="{ classes }"
                immediate
                vid="greenClaim"
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.greenClaim"
                    :value="
                      row.greenClaim === undefined
                        ? (row.greenClaim = ' ')
                        : row.greenClaim
                    "
                    class="greenClaim"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'greenClaim',
                        row.greenClaim
                      )
                    "
                  >
                    <option value=" "></option>
                    <option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td>
              <ValidationProvider
                rules="required_if:greenClaim,Y"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    class="greenClaim1"
                    :disabled="row.greenClaim !== 'Y'"
                    v-bind:class="[
                      isApiErrorField(
                        row.psfsEuNewItemGiErrTbs,
                        'greenClaim1',
                        row.greenClaim1
                      ),
                      checkGreenClaim1(row)
                    ]"
                    v-model="row.greenClaim1"
                    :title="row.greenClaim1"
                  >
                    <option value=" "></option>
                    <option value="ACTLABEL">ACT Label Available</option>
                    <option value="CERTSEAP"
                      >Certifications and Seals of Approval</option
                    >
                    <option value="COMP">Compostable</option>
                    <option value="DEGR">Degradable</option>
                    <option value="ENERGY">Energy Star Certification</option>
                    <option value="FREEOF">Free-of Claims</option>
                    <option value="NONT">Non-Toxic Claims</option>
                    <option value="NS">No Claim</option>
                    <option value="OZFREE"
                      >Ozone-Safe / Ozone-Friendly Claims</option
                    >
                    <option value="RECY">Recyclable</option>
                    <option value="RECYCON">Recycled Content</option>
                    <option value="RNWEN">Renewable Energy Claims</option>
                    <option value="RNWMAT">Renewable Materials Claims</option>
                    <option value="SOURCE">Source Reduction</option>
                  </b-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-select
                class="greenClaim2"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim2',
                    row.greenClaim2
                  )
                ]"
                v-model="row.greenClaim2"
                :title="row.greenClaim2"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim3"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim3',
                    row.greenClaim3
                  )
                ]"
                v-model="row.greenClaim3"
                :title="row.greenClaim3"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>
                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim4"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim4',
                    row.greenClaim4
                  )
                ]"
                v-model="row.greenClaim4"
                :title="row.greenClaim4"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim5"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim5',
                    row.greenClaim5
                  )
                ]"
                v-model="row.greenClaim5"
                :title="row.greenClaim5"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim6"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim6',
                    row.greenClaim6
                  )
                ]"
                v-model="row.greenClaim6"
                :title="row.greenClaim6"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim7"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim7',
                    row.greenClaim7
                  )
                ]"
                v-model="row.greenClaim7"
                :title="row.greenClaim7"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim8"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim8',
                    row.greenClaim8
                  )
                ]"
                v-model="row.greenClaim8"
                :title="row.greenClaim8"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim9"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim9',
                    row.greenClaim9
                  )
                ]"
                v-model="row.greenClaim9"
                :title="row.greenClaim9"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
            <td>
              <b-select
                class="greenClaim10"
                :disabled="row.greenClaim !== 'Y'"
                v-bind:class="[
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'greenClaim10',
                    row.greenClaim10
                  )
                ]"
                v-model="row.greenClaim10"
                :title="row.greenClaim10"
              >
                <option value=" "></option>
                <option value="ACTLABEL">ACT Label Available</option>

                <option value="CERTSEAP"
                  >Certifications and Seals of Approval</option
                >
                <option value="COMP">Compostable</option>
                <option value="DEGR">Degradable</option>
                <option value="ENERGY">Energy Star Certification</option>
                <option value="FREEOF">Free-of Claims</option>
                <option value="NONT">Non-Toxic Claims</option>
                <option value="NS">No Claim</option>
                <option value="OZFREE"
                  >Ozone-Safe / Ozone-Friendly Claims</option
                >
                <option value="RECY">Recyclable</option>
                <option value="RECYCON">Recycled Content</option>
                <option value="RNWEN">Renewable Energy Claims</option>
                <option value="RNWMAT">Renewable Materials Claims</option>
                <option value="SOURCE">Source Reduction</option>
              </b-select>
            </td>
          </ValidationObserver>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "GeneralInfoTab",
  components: { vSelect },
  data() {
    return {
      checkboxGroup: [],
      dateAvailable: null,
      generalInfoFields: {
        columns: [
          {
            className: "lineErrors",
            field: "lineErrors",
            label: "Line Errors",
            title: "Line Errors"
          },
          {
            className: "totalLineErrorCount",
            field: "totalLineErrorCount",
            label: "Total Line Errors",
            title: "Total Line Errors"
          },
          {
            className: "suppItemNbr",
            field: "suppItemNbr",
            label: "Supplier Item No.",
            title: "Supplier Item No.\nMax length is 25."
          },
          {
            className: "itemStatus",
            field: "itemStatus",
            label: "Item Status",
            title: "Item Status"
          },
          {
            className: "productCategory",
            field: "productCategory",
            label: "Product category/Item Class",
            title: "Select the category of product"
          },
          {
            className: "manufacturerItemNbr",
            field: "manufacturerItemNbr",
            label: "Manufacturer item No.",
            title: "Manufacturer item No."
          },
          {
            className: "manufacturerName",
            field: "manufacturerName",
            label: "Manufacturer name",
            title: "Manufacturer name"
          },
          {
            className: "suppBrandName",
            field: "suppBrandName",
            label: "Supplier Brand name",
            title: "Supplier Brand name by which product should be known"
          },
          {
            className: "mainPackType",
            field: "mainPackType",
            label: "Main Pack type",
            title: "Type of the main pack (smaller selling pack)"
          },
          {
            className: "mainPackSize",
            field: "mainPackSize",
            label: "Main Pack size",
            title: "Quantity in the smaller selling unit"
          },
          {
            className: "mainPackUnit",
            field: "mainPackUnit",
            label: "Main Pack unit",
            title: "Description of the amount (e.g. mL, Pieces)"
          },
          {
            className: "altPackSizeType",
            field: "altPackSizeType",
            label: "Alternate pack size type",
            title: ""
          },
          {
            className: "altPackSizeConv",
            field: "altPackSizeConv",
            label: "Alternate pack size conversion factor",
            title: "Number of main Unit in the alternate pack size"
          },
          {
            className: "englishPrdctNoun",
            field: "englishPrdctNoun",
            label: "English Product Description",
            title: "Product Description"
          },
          {
            className: "addLangPrdctDessc",
            field: "addLangPrdctDessc",
            label: "Additional Language Code",
            title: ""
          },
          {
            className: "addPrdctNoun",
            field: "addPrdctNoun",
            label: "Additional Product Description",
            title: ""
          },
          {
            className: "minOrdrQty",
            field: "minOrdrQty",
            label: "Minimum order quantity",
            title: "Normally 1, unless purchase in larger cases"
          },
          {
            className: "suppLeadTime",
            field: "suppLeadTime",
            label: "Lead Time from Supplier",
            title: "Standard supplier lead time to Fisher (working days)"
          },
          {
            className: "minStorageTemp",
            field: "minStorageTemp",
            label: "Minimum Storage Temperature",
            title: "Indicate Minimum storage temperature in C°"
          },
          {
            className: "maxStorageTemp",
            field: "maxStorageTemp",
            label: "Maximum Storage Temperature",
            title: "Indicate Maximum storage temperature in C°"
          },
          {
            className: "coolant",
            field: "coolant",
            label: "Coolant Needed For Shipment",
            title:
              "Legacy Coolant on storage code listing. Indicate if coolant is required for shipment (Wet ice, Dry Ice etc). Room Temp if none"
          },
          {
            className: "sample",
            field: "sample",
            label: "Demo or Sample",
            title: "Select Demo or Sample if item is one of"
          },
          {
            className: "euCmmdtyCode",
            field: "euCmmdtyCode",
            label: "EU Commodity code",
            title: "8 digit EU code"
          },
          {
            className: "country",
            field: "country",
            label: "Country of origin",
            title: "Country in which the product originates"
          },
          {
            className: "serialNbr",
            field: "serialNbr",
            label: "Serial number tracking",
            title: "Should the serial number be logged (Y/N)"
          },
          {
            className: "batchCntrl",
            field: "batchCntrl",
            label: "Batch control",
            title: "Should the batch number be logged (Y/N)"
          },
          {
            className: "shlfLf",
            field: "shlfLf",
            label: "Shelf Life (If required)",
            title:
              "If the item has an expiry date indicate the normal shelf life in days"
          },
          {
            className: "warranty",
            field: "warranty",
            label: "Warranty (If required)",
            title:
              "Length of  warranty if there is one (years) - Blank if no warranty"
          },
          {
            className: "returnable",
            field: "returnable",
            label: "Returnable?",
            title: "Can this item be returned"
          },
          {
            className: "mainUnitGrossWt",
            field: "mainUnitGrossWt",
            label: "Gross weight of Main unit",
            title:
              "Gross weight (KG) of the item - 4 decimals - 0.0001 kg is the minimum"
          },
          {
            className: "mainUnitNetWt",
            field: "mainUnitNetWt",
            label: "Net weight of Main unit",
            title:
              "Net weight (KG) of the item - 4 decimals - 0.0001 kg is the minimum"
          },
          {
            className: "mainUnitGrossVol",
            field: "mainUnitGrossVol",
            label: "Gross volume of main unit",
            title: "Warehouse use; Gross volume (M3)"
          },
          {
            className: "mainUnitNetVol",
            field: "mainUnitNetVol",
            label: "Net Volume of main unit",
            title: "Net volume (M3)"
          },
          {
            className: "mainUnitPackLen",
            field: "mainUnitPackLen",
            label: "Packaging length of main unit",
            title: "Warehouse use; Length (m)"
          },
          {
            className: "mainUnitPackHt",
            field: "mainUnitPackHt",
            label: "Packaging height of main unit",
            title: "Warehouse use; Height (m)"
          },
          {
            className: "mainUnitPackWd",
            field: "mainUnitPackWd",
            label: "Packaging width of main unit",
            title: "Warehouse use; Width (m)"
          },
          {
            className: "altUnitGrossWt",
            field: "altUnitGrossWt",
            label: "Gross weight of Alternate unit",
            title:
              "Gross weight (KG) of the item - 4 decimals - 0.0001 kg is the minimum"
          },
          {
            className: "altUnitNetWt",
            field: "altUnitNetWt",
            label: "Net weight of alternate unit",
            title:
              "Net weight (KG) of the item - 4 decimals - 0.0001 kg is the minimum"
          },
          {
            className: "altUnitGrossVol",
            field: "altUnitGrossVol",
            label: "Gross volume of alternate unit",
            title: "Warehouse use; Gross volume (M3)"
          },
          {
            className: "altUnitPackLen",
            field: "altUnitPackLen",
            label: "Packaging length of alternate unit",
            title: "Warehouse use; Length (m)"
          },
          {
            className: "altUnitPackHt",
            field: "altUnitPackHt",
            label: "Packaging height of alternate unit",
            title: "Warehouse use; Height (m)"
          },
          {
            className: "altUnitPackWd",
            field: "altUnitPackWd",
            label: "Packaging width of alternate unit",
            title: "Warehouse use; Width (m)"
          },
          {
            className: "dateAvailable",
            field: "dateAvailable",
            label: "Available from date",
            title:
              "When product will be released. If blank we consider product already available."
          },
          {
            className: "greenClaim",
            field: "greenClaim",
            label: "Does this product have a GREEN Claim?",
            title: "Does this product have a GREEN Claim?"
          },
          {
            className: "greenClaim1",
            field: "greenClaim1",
            label: "Green Claim 1",
            title:
              "Select one Green claim. If Green Claim = 'ACTLABEL', one other Green Claim must be entered"
          },
          {
            className: "greenClaim2",
            field: "greenClaim2",
            label: "Green Claim 2",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim3",
            field: "greenClaim3",
            label: "Green Claim 3",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim4",
            field: "greenClaim4",
            label: "Green Claim 4",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim5",
            field: "greenClaim5",
            label: "Green Claim 5",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim6",
            field: "greenClaim6",
            label: "Green Claim 6",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim7",
            field: "greenClaim7",
            label: "Green Claim 7",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim8",
            field: "greenClaim8",
            label: "Green Claim 8",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim9",
            field: "greenClaim9",
            label: "Green Claim 9",
            title: "Select one Green claim"
          },
          {
            className: "greenClaim10",
            field: "greenClaim10",
            label: "Green Claim 10",
            title: "Select one Green claim"
          }
        ]
      },
      isLoading: false,
      loadDetails: false,
      actions: [],
      minDateAvailable: new Date(),
      paginationParams: [],
      rowIds: [],
      rows: [],
      selectAllChecked: [],
      showSubmissionInReadOnly: false,
      suppItemNbrRequired: false,
      tooltipIndex: "",
      typeAheadTriggered: "",
      countryOptions: [],
      euCmmdtyCodeOptions: [],
      mainPackTypeOptions: [],
      mainPackUnitOptions: [],
      manufacturerNameOptions: [],
      suppBrandNameOptions: []
    };
  },
  props: ["giTabSubmissionID", "loadGITabData", "onAddNewRow"],
  watch: {
    onAddNewRow(value) {
      this.addNewRow(value);
    },
    loadGITabData(value) {
      if (value.loadDetails) {
        this.actions = value.actions;
        this.paginationParams = value.paginationParams;
        this.loadTabItemDetails();
      }
    },
    selectAllChecked: function(isChecked) {
      if (isChecked) {
        this.checkboxGroup = this.rowIds;
      } else {
        this.checkboxGroup = [];
      }
    }
  },
  mounted() {
    // var scope = this;
    this.$root.$on("rowIdsForDeleteAction", value => {
      this.deleteItemByIds(value);
    });
    this.loadPackUnitOptions();
  },
  methods: {
    sanitizeProductionDescription(value) {
        return String(value ?? '')
          .replace(/[|"]/g, '')
          .slice(0, 94);
    },
    addThreeZeros(value) {
      return parseFloat(value).toFixed(
        Math.max(((value + "").split(".")[1] || "").length, 3)
      );
    },
    addTwoZeros(value) {
      return parseFloat(value).toFixed(
        Math.max(((value + "").split(".")[1] || "").length, 2)
      );
    },
    addNewRow(value) {
      if (value) {
        try {
          this.rows.splice(this.rows.length + 1, 0, {});
          this.$emit("setNewRowToFalse", true);
        } catch (e) {
          this.$buefy.dialog.alert({
            message:
              "Failed to add item. Please contact the New Item Support at NewItem.IT.Support@thermofisher.com",
            confirmText: "OK",
            type: "is-danger"
          });
        }
      }
    },
    validateStorageTempInput(e,row,field) {
       let val = e.target.value;
       val = val.replace(/[^\d-]/g, '') // remove invalid chars
       .replace(/(?!^)-/g, ''); // only allow "-" at the start
       e.target.value = val;
       row[field] = val;
    },
    addPercentMode(value) {
      return parseFloat(value).toFixed(
        Math.max(((value + "").split(".")[1] || "").length, 4)
      );
    },
    changeOnRow(event, index, rowData) {
      if (
        rowData.suppItemNbr === undefined ||
        rowData.suppItemNbr.trim() === "" ||
        rowData.suppItemNbr === null
      ) {
        this.suppItemNbrRequired = true;
        this.tooltipIndex = index;
      } else {
        this.suppItemNbrRequired = false;
        this.tooltipIndex = "";
        var rowArrayFromResponse = JSON.parse(
          sessionStorage.getItem("giEuRows")
        );
        if (rowArrayFromResponse && rowArrayFromResponse.length > 0) {
          var rowDataFromResponseObj = JSON.parse(
            sessionStorage.getItem("giEuRows")
          )[index];
          for (var key in (rowDataFromResponseObj, rowData)) {
            if (
              key !== "psfsEuNewItemGiErrTbs" &&
              key !== "lineNumber" &&
              rowDataFromResponseObj[key] !== rowData[key]
            ) {
              //logic for dateAvailable field
              if (key === "dateAvailable" && rowDataFromResponseObj[key]) {
                // rowDataFromResponseObj[key] = new Date(rowDataFromResponseObj[key]);
                if (
                  !(
                    rowDataFromResponseObj[key] !== rowData[key] &&
                    new Date(rowDataFromResponseObj[key]) ===
                      new Date(rowData[key])
                  )
                ) {
                  this.updateGiItem(rowData, index);
                  break;
                }
              } else if (
                key === "manufacturerName" ||
                key === "suppBrandName"
              ) {
                if (rowData[key] && rowData[key].label) {
                  rowData[key] = rowData[key].label;
                }
                this.updateGiItem(rowData, index);
                break;
              } else {
                if (
                  !(
                    rowDataFromResponseObj[key] === undefined &&
                    rowData[key] === " "
                  )
                ) {
                  if (rowData[key] === " ") {
                    rowData[key] = "";
                  }
                  this.updateGiItem(rowData, index);
                  break;
                }
              }
            }
          }
        }
      }
    },
    checkGreenClaim1(rowData) {
      if (
        rowData.greenClaim1 === "ACTLABEL" &&
        !(
          rowData.greenClaim2 ||
          rowData.greenClaim3 ||
          rowData.greenClaim4 ||
          rowData.greenClaim5 ||
          rowData.greenClaim6 ||
          rowData.greenClaim7 ||
          rowData.greenClaim8 ||
          rowData.greenClaim9 ||
          rowData.greenClaim10
        )
      ) {
        return "is-danger";
      }
    },
    copyCountryToClipBoard(text) {
      const el = document.createElement("textarea");
      el.value = text.split(" ")[0];
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.$buefy.snackbar.open({
        duration: 2000,
        message: "Copied text to clipboard",
        type: "is-success",
        position: "is-top"
      });
    },
    copyRowItems(row) {
      this.$buefy.dialog.prompt({
        message: `Enter number of Items to copy:`,
        inputAttrs: {
          type: "number",
          max: 100,
          min: 1
        },
        trapFocus: true,
        onConfirm: value => this.copyItems(value, row)
      });
    },
    copyItems(number, rowData) {
      var scope = this;

      var createdBy = JSON.parse(sessionStorage.getItem("se_ni_user_eu"))
        .networkId;

      scope.$http
        .post(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/copyLineInfo?nbrNewItemCrKey=${scope.giTabSubmissionID}&noOfRows=${number}&genlInfoId=${rowData.genlInfoId}&networkId=${createdBy}`
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.loadTabItemDetails();
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Copied selected item succesfully.`,
            type: "is-success",
            position: "is-top",
            queue: false
          });
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to Copy item information for Submission - ${scope.giTabSubmissionID}. Please check for invalid data and/or clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        });
    },
    deleteItemByIds() {
      var scope = this;
      var args = {
        headers: {
          "content-type": "application/json"
        }
      };

      scope.isLoading = true;
      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/deleteSubmissionItems?nbrNewItemCrKey=${scope.giTabSubmissionID}`,
          this.checkboxGroup,
          args
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.$emit("deleteItemsComplete, true");
          scope.selectAllChecked = false;
          scope.checkboxGroup = [];
          scope.loadTabItemDetails();
          scope.$buefy.snackbar.open({
            message: `Delete successful on selected items under Submission ID - ${scope.giTabSubmissionID}.`,
            type: "is-success",
            position: "is-top",
            actionText: "OK"
          });
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            message: `Failed to delete selected items under Submission ID - ${scope.giTabSubmissionID}. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            actionText: "OK"
          });
        });
    },
    fetchSupplierTypeAhead(search, loading) {
      if (search.length > 0) {
        var scope = this;
        var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));
        var suppNbr = userData.supplierId;

        loading(true);
        scope.$http
          .get(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/loadSupplierDropDownValues?suppNbr=${suppNbr}&fieldName=${scope.typeAheadTriggered}&searchName=${search}`
          )
          .then(function(response) {
            loading(false);
            switch (scope.typeAheadTriggered) {
              case "manufacturerName":
                scope.manufacturerNameOptions = response.data;
                break;
              case "suppBrandName":
                scope.suppBrandNameOptions = response.data;
                break;
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch(function(error) {
            loading(false);
            scope.$buefy.dialog.alert({
              message: `Failed to load matching results. Please enter valid search criteria and/or clear the cache and try again. If the problem persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger"
            });
          });
      }
    },
    fetchTypeAhead(search, fieldName) {
      if (search.length > 0) {
        var scope = this;
        scope.isLoading = true;
        scope.$http
          .get(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/loadTypeaheadsCodes?fieldName=${fieldName}&code=${search}`
          )
          .then(function(response) {
            scope.isLoading = false;
            switch (fieldName) {
              case "packType":
                scope.mainPackTypeOptions = response.data;
                break;
              case "packUnit":
                scope.mainPackUnitOptions = response.data;
                break;
              case "country":
                scope.countryOptions = response.data;
                break;
              case "euCmmdtyCode":
                scope.euCmmdtyCodeOptions = response.data;
                break;
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `Failed to load matching results. Please enter valid search criteria and/or clear the cache and try again. If the problem persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger"
            });
          });
      }
    },
    insertNewRow(value) {
      var scope = this;
      var requestPayload = {};
      var userData = JSON.parse(sessionStorage.getItem("se_ni_user_eu"));
      requestPayload.createdBy = userData.networkId;
      requestPayload.suppItemNbr = value;
      requestPayload.createdBy = userData.networkId;

      scope.suppItemNbrRequired = false;
      scope.tooltipIndex = "";

      scope.isLoading = true;
      scope.$http
        .post(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/createLineInfo?nbrNewItemCrKey=${scope.giTabSubmissionID}`,
          requestPayload
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.loadTabItemDetails();
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to add new item fto Submission - ${scope.giTabSubmissionID}. Please check for invalid data and/or clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        });
    },
    // eslint-disable-next-line no-unused-vars
    isApiErrorField(errorFieldNames, className, value) {
      if (errorFieldNames && errorFieldNames.length > 0) {
        for (var i = 0; i < errorFieldNames.length; i++) {
          if (
            errorFieldNames[i].columnName
              .split(" ")[0]
              .replace(/_/g, "")
              .toLowerCase() === className.toLowerCase()
          ) {
            return "api-error";
          }
        }
      }
    },
    onRowClick(event) {
      var tableRows = event.target.closest("tr").parentElement.rows;
      for (var row in tableRows) {
        if (
          tableRows[row].classList &&
          tableRows[row].classList.contains("highlight-row")
        ) {
          tableRows[row].classList.remove("highlight-row");
        }
      }
      event.target.closest("tr").classList.add("highlight-row");
    },
    loadPackUnitOptions() {
      var scope = this;

      scope.isLoading = true;
      scope.$http
        .get(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/DropDownValues?fieldName=packUnit`
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.mainPackUnitOptions = response.data;
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to load Main Pack Unit options. Please try again later. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        });
    },
    loadTabItemDetails() {
      var scope = this;
      scope.isLoading = true;
      var args = {
        headers: {
          "content-type": "application/json"
        }
      };

      var requestPayload = {};
      requestPayload.actions = [];
      requestPayload.filterMap = {};
      requestPayload.searchMap = {};
      requestPayload.sortMap = {};
      if (
        this.actions.fErrors ||
        this.actions.fSuppItemNbr ||
        this.actions.fProductNoun ||
        this.actions.fProdCtgry ||
        this.actions.fReworkItem
      ) {
        requestPayload.actions.push("Filter");
        requestPayload.filterMap = {};
        if (this.actions.fErrors) {
          requestPayload.filterMap.ErroredItems = this.actions.fErrors;
        }
        if (this.actions.fSuppItemNbr) {
          requestPayload.filterMap.suppItemNbr = this.actions.fSuppItemNbr;
        }
        if (this.actions.fProductNoun) {
          requestPayload.filterMap.englishPrdctNoun = this.actions.fProductNoun;
        }
        if (this.actions.fProdCtgry) {
          requestPayload.filterMap.productCategory = this.actions.fProdCtgry;
        }
        if (this.actions.fReworkItem) {
          requestPayload.filterMap.itemStatus = "K";
        }
      }
      if (this.actions.sortSuppItemNbr) {
        requestPayload.actions.push("Sort");
        requestPayload.sortMap = {};
        requestPayload.sortMap.suppItemNbr = this.actions.sortSuppItemNbr;
      }
      if (this.actions.srFieldName && this.actions.srFieldValue) {
        requestPayload.actions.push("Search");
        requestPayload.searchMap = {};
        requestPayload.searchMap[
          this.actions.srFieldName
        ] = this.actions.srFieldValue;
      }

      scope.$http
        .post(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/getGeneralInfo?nbrNewItemCrKey=${scope.giTabSubmissionID}&pageIndex=${scope.paginationParams.pageIndex}&noOfRecords=${scope.paginationParams.numberOfItems}`,
          requestPayload,
          args
        )
        .then(function(response) {
          var giPaginationParams = {
            startIndex: response.data.startIndex,
            endIndex: response.data.endIndex,
            totalCount: response.data.totalCount,
            totalPages: response.data.totalPages
          };
          scope.$emit("giPaginationParams", giPaginationParams);
          scope.$emit("giCount", {
            totalErrorCount: response.data.errorCount,
            tabErrorCount: response.data.giTabErrorCount
          });

          //check supplier validate value
          if (sessionStorage.getItem("se_ni_user_eu")) {
            if (response.data.validateForm === "Y") {
              scope.$emit("disableSupplierValidateFromTab", true);
            } else {
              scope.$emit("enableSupplierValidateFromTab", true);
            }
          }

          scope.isLoading = false;
          scope.rows = [];
          scope.rowIds = [];
          var mutableResponseRows = response.data.psfsEuNewItemGiTbs;
          if (requestPayload.actions.length > 0) {
            if (response.data.psfsEuNewItemGiTbs.length > 0) {
              for (
                var i = 0, lineNumber = giPaginationParams.startIndex;
                i < mutableResponseRows.length && lineNumber > 0;
                i++, lineNumber++
              ) {
                scope.rowIds.push(mutableResponseRows[i].genlInfoId);
                mutableResponseRows[i].lineNumber = lineNumber;
                if (mutableResponseRows[i].dateAvailable) {
                  mutableResponseRows[i].dateAvailable = new Date(
                    mutableResponseRows[i].dateAvailable
                  );
                }
              }
              scope.rows = mutableResponseRows;
              sessionStorage.setItem(
                "giEuRows",
                JSON.stringify(response.data.psfsEuNewItemGiTbs)
              );
            } else {
              scope.rows = [];
              scope.rowIds = [];
            }
          } else {
            if (response.data.psfsEuNewItemGiTbs.length > 0) {
              for (
                var j = 0, lineNumberJ = giPaginationParams.startIndex;
                j < mutableResponseRows.length && lineNumberJ > 0;
                j++, lineNumberJ++
              ) {
                scope.rowIds.push(mutableResponseRows[j].genlInfoId);
                mutableResponseRows[j].lineNumber = lineNumberJ;
                if (mutableResponseRows[j].dateAvailable) {
                  mutableResponseRows[j].dateAvailable = new Date(
                    mutableResponseRows[j].dateAvailable
                  );
                }
              }
              scope.rows = mutableResponseRows;
              sessionStorage.setItem(
                "giEuRows",
                JSON.stringify(response.data.psfsEuNewItemGiTbs)
              );
            } else {
              scope.rows = [];
              scope.rowIds = [];
              sessionStorage.removeItem("rows");
              scope.addNewRow(true);
            }
          }
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to load items for Submission - ${scope.giTabSubmissionID}. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        });
    },
    updateGiItem(requestPayload, index) {
      var scope = this;
      var args = {
        headers: {
          "content-type": "application/json"
        }
      };

      if (sessionStorage.getItem("se_ni_user_eu")) {
        requestPayload.createdBy = JSON.parse(
          sessionStorage.getItem("se_ni_user_eu")
        ).networkId;
      } else {
        requestPayload.createdBy = window.$cookies.get("ni_key").networkId;
      }

      var updatedRowData = {};

      for (var key in requestPayload) {
        var prop = "";
        prop = requestPayload[key];
        if (typeof prop === "string") {
          if (prop.trim() !== "") {
            updatedRowData[key] = requestPayload[key];
          }
        } else {
          if (key === "dateAvailable") {
            updatedRowData[key] = new Date(requestPayload[key]);
          } else {
            updatedRowData[key] = requestPayload[key];
          }
        }
      }

      scope.isLoading = true;
      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/updateLineInfo?nbrNewItemCrKey=${scope.giTabSubmissionID}&genlInfoId=${updatedRowData.genlInfoId}`,
          updatedRowData,
          args
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          var apiRow = response.data;
          apiRow.lineNumber = scope.rows[index].lineNumber;
          scope.rows[index] = apiRow;
          var giEuRows = JSON.parse(sessionStorage.getItem("giEuRows"));
          giEuRows.splice(index, 1, response.data);
          sessionStorage.setItem("giEuRows", JSON.stringify(giEuRows));
          scope.$emit("giCount", {
            totalErrorCount: response.data.errorCount,
            tabErrorCount: response.data.giTabErrorCount
          });
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to update item information for Submission - ${scope.giTabSubmissionID}. Please check for invalid data and/or clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        })
        .then(function() {
          var tableRows = document.querySelector(
            ".ni-app-general-info-tbody-eu"
          ).rows;
          for (var row in tableRows) {
            if (
              tableRows[row].classList &&
              tableRows[row].classList.contains("highlight-row")
            ) {
              tableRows[row].classList.remove("highlight-row");
            }
          }
          //add highlight-row class
          document
            .querySelector(".ni-app-general-info-tbody-eu")
            .rows[index].classList.add("highlight-row");
          //blur select event on active element wrt index
          if (
            document.activeElement.closest("tr") &&
            document.activeElement.closest("tr").rowIndex !== index + 1
          ) {
            document.activeElement.blur();
          }
        });
    }
  }
};
</script>
